import { useCallback, useEffect, useRef, useState } from 'react';
import { EngineError, login, me } from '../api/engineDashboard';
import {
  clearDashboardSession,
  loadDashboardSession,
  saveDashboardSession,
  type DashboardSession,
} from '../lib/dashboardSession';

type State =
  | { status: 'idle'; session: DashboardSession | null }
  | { status: 'signing-in'; session: null }
  | { status: 'error'; session: null; error: string };

export interface DashboardAuthApi {
  state: State;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  /** Swap in a session obtained outside the login form (impersonation). */
  adoptSession: (session: DashboardSession) => void;
  /** Call when any Engine request comes back 401 — drops to the login page. */
  onUnauthorized: () => void;
}

/**
 * Email + password auth against the Resolution Engine for the dashboard
 * render mode (browser tab or Zoom main client). Stores the session JWT in
 * localStorage; on mount, revalidates a stored token via GET /auth/me so a
 * revoked/expired token drops cleanly to the login page instead of failing
 * on the first data fetch.
 */
export function useDashboardAuth(): DashboardAuthApi {
  const [state, setState] = useState<State>(() => ({
    status: 'idle',
    session: loadDashboardSession(),
  }));

  // Revalidate the stored token once on mount.
  useEffect(() => {
    const session = loadDashboardSession();
    if (!session) return;
    let cancelled = false;
    me(session.token).catch((err: unknown) => {
      if (cancelled) return;
      if (err instanceof EngineError && (err.status === 401 || err.status === 403)) {
        clearDashboardSession();
        setState({ status: 'idle', session: null });
      }
      // Network errors: keep the session; data fetches will surface issues.
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setState({ status: 'signing-in', session: null });
    try {
      const res = await login(email.trim(), password);
      const session: DashboardSession = {
        token: res.token,
        expiresAt: Date.now() + res.expires_in * 1000,
        principal: res.principal,
      };
      saveDashboardSession(session);
      setState({ status: 'idle', session });
    } catch (err) {
      setState({
        status: 'error',
        session: null,
        error:
          err instanceof EngineError
            ? err.message
            : 'Could not reach the Levelheaded service. Check your connection and try again.',
      });
    }
  }, []);

  const adoptSession = useCallback((session: DashboardSession) => {
    saveDashboardSession(session);
    setState({ status: 'idle', session });
  }, []);

  const signOut = useCallback(() => {
    clearDashboardSession();
    setState({ status: 'idle', session: null });
  }, []);

  // A single 401 — often from a 20s background poll — used to clear the
  // session outright, yanking the user to the login screen mid-work (Rachel,
  // 2026-10-08: "had to sign back in, maybe work was happening in the
  // background"). Re-verify the token first: clear ONLY when the server
  // confirms it's dead (401/403 on /me). A transient 401 keeps the session.
  const reverifying = useRef(false);
  const onUnauthorized = useCallback(() => {
    const session = loadDashboardSession();
    if (!session) {
      setState({ status: 'idle', session: null });
      return;
    }
    if (reverifying.current) return;
    reverifying.current = true;
    me(session.token)
      .then(() => {
        // Token is actually valid — the 401 was transient; keep the session.
      })
      .catch((err: unknown) => {
        if (err instanceof EngineError && (err.status === 401 || err.status === 403)) {
          clearDashboardSession();
          setState({ status: 'idle', session: null });
        }
        // Network error: keep the session, let the next action re-check.
      })
      .finally(() => {
        reverifying.current = false;
      });
  }, []);

  return { state, signIn, signOut, adoptSession, onUnauthorized };
}
